import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Button, Heading, Icon, Modal } from '@ui/themed';
import { Background, Panel, ReactFlowProvider } from '@xyflow/react';
import { t } from 'i18next';
import { useCallback, useMemo, useState } from 'react';
import {
  type Adjacency,
  type AdjacencyList,
  Forest,
  type NodeData,
} from '../../../components/forest';
import {
  openHandleID,
  trimNodeID,
  useSectionTreeMutations,
} from '../hooks/use-section-tree-mutations';
import type { SectionResponse } from '../types/api';
import type { CustomerMinimal } from '../types/company';
import type {
  SectionTreeEdge,
  SectionTreeEdgeType,
  SectionTreeEdgeTypeID,
  SectionTreeNode,
  SectionTreeNodeType,
  SectionTreeNodeTypeID,
} from '../types/section-tree';
import { AddCompanyUser } from './add-company-user';
import CustomerEdgeType from './node-edge-types/CustomerEdgeType';
import CustomerNodeType from './node-edge-types/CustomerNodeType';
import SectionNodeType from './node-edge-types/SectionNodeType';
import { SectionForm, sectionFormCreateDefaultValues } from './section-form';

const nodeTypes: Record<SectionTreeNodeTypeID, SectionTreeNodeType> = {
  section: SectionNodeType,
  customer: CustomerNodeType,
};

const edgeTypes: Record<SectionTreeEdgeTypeID, SectionTreeEdgeType> = {
  section: {
    labelComponent: (_) => <></>,
    colour: 'var(--color-spark-text-secondary)',
    selectedColour: 'var(--color-spark-purple)',
    width: 3,
    markerEnd: { type: 'arrowclosed', color: 'var(--color-spark-text-secondary)' },
  },
  customer: CustomerEdgeType,
  connection: {
    labelComponent: (_) => <></>,
    colour: 'var(--color-spark-text-secondary)',
    selectedColour: 'var(--color-spark-purple)',
    dashed: '10,10',
    width: 3,
    opacity: 0.5,
  },
};

export function SectionTree({
  companyId,
  sections,
}: {
  companyId: string;
  sections: SectionResponse[];
}): JSX.Element {
  const {
    floatingCustomers,
    sectionChildren,
    sectionUsers,
    useAddCompanyUser,
    useConnect,
    useRemoveEdge,
    useRemoveNode,
    isValidConnection,
  } = useSectionTreeMutations(companyId, sections);

  const [addModalState, setAddModalState] = useState<null | {
    key: 'section' | 'customer';
    parent: string | null;
    sibling: string | null;
  }>(null);

  const { nodes, edges } = useMemo(() => {
    const sectionNodes: NodeData<SectionTreeNode, SectionTreeNodeTypeID>[] = sections.map(
      (section) => ({
        ...section,
        id: `section:${section.id}`,
        company_id: companyId,
        type: 'section',
        handles: (sectionChildren[section.id] ?? [])
          .map((id) => `section:${id}`)
          .concat(section.company_users.map(({ customer }) => `customer:${customer.id}`))
          .concat(openHandleID),
      }),
    );

    const customerNodes: NodeData<SectionTreeNode, SectionTreeNodeTypeID>[] = sections
      .flatMap(({ company_users }) => company_users.map(({ customer }) => customer))
      .concat(floatingCustomers)
      .map((customer) => ({
        ...customer,
        id: `customer:${customer.id}`,
        type: 'customer',
        handles: [],
      }));

    const nodes = sectionNodes.concat(customerNodes);

    const edges = sections.reduce(
      (a, x) => {
        const mapping: Record<string, Adjacency<SectionTreeEdge, SectionTreeEdgeTypeID>> = {};

        for (const id of sectionChildren[x.id] ?? []) {
          mapping[`section:${id}`] = {
            childID: `section:${id}`,
            type: 'section',
          };
        }

        for (const {
          customer: { id },
          role,
        } of x.company_users) {
          mapping[`customer:${id}`] = {
            childID: `customer:${id}`,
            type: 'customer',
            data: role,
          };
        }

        a[`section:${x.id}`] = mapping;

        return a;
      },
      {} as AdjacencyList<SectionTreeEdge, SectionTreeEdgeTypeID>,
    );
    return { nodes, edges };
  }, [floatingCustomers, sectionChildren, sections, companyId]);

  const handleReconnect = useCallback(
    (
      _oldSourceID: string,
      _oldSourceHandle: string,
      _oldTargetID: string,
      newSourceID: string,
      newSourceHandle: string,
      newTargetID: string,
    ) =>
      useConnect.mutate({
        parentNodeId: newSourceID,
        handleId: newSourceHandle,
        childNodeId: newTargetID,
      }),
    [useConnect],
  );

  const handleAddNodeOnDrop = useCallback(
    (parentNodeID: string, handleID: string) => {
      const parent = trimNodeID(parentNodeID);
      if (parent === null || parent.type !== 'section') {
        return;
      }
      const handle = trimNodeID(handleID);
      setAddModalState({
        key: sectionUsers[parent.id]?.length ? 'customer' : 'section',
        parent: parent.id,
        sibling: handle && handle.type === 'section' ? handle.id : null,
      });
    },
    [sectionUsers],
  );

  const handleAddCompanyUser = useCallback(
    (customer: CustomerMinimal) => {
      if (addModalState?.key === 'section') {
        return;
      }
      useAddCompanyUser.mutate({
        sectionId: addModalState?.parent ?? null,
        customer,
      });
      setAddModalState(null);
    },
    [addModalState, useAddCompanyUser],
  );

  return (
    <ReactFlowProvider>
      <Forest
        canvas_width="100%"
        canvas_height="100%"
        block_width={400}
        block_height={250}
        vertical
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onRemoveEdge={(parentNodeId, handleId, childNodeId) =>
          useRemoveEdge.mutate({
            parentNodeId,
            handleId,
            childNodeId,
          })
        }
        onRemoveNode={(id) => useRemoveNode.mutate(id)}
        onConnect={(parentNodeId, handleId, childNodeId) =>
          useConnect.mutate({
            parentNodeId,
            handleId,
            childNodeId,
          })
        }
        onReconnect={handleReconnect}
        isValidConnection={isValidConnection}
        onAddNodeDrop={handleAddNodeOnDrop}
        minimumAddNodeDistance={200}
        inHandleClass="bg-spark-purple! h-3! w-3!"
        connectionLineEdgeType="connection"
      >
        <Background />
        <Panel position="top-right">
          <Button
            onClick={() =>
              setAddModalState((current) =>
                current
                  ? { ...current, key: 'customer' }
                  : { key: 'customer', sibling: null, parent: null },
              )
            }
          >
            <div className="flex gap-2">
              <Icon name="userAdd" />
              {t('features:sections.components.section_tree.add_company_user')}
            </div>
          </Button>
        </Panel>
      </Forest>
      <Modal
        isOpen={addModalState !== null}
        onClose={() => setAddModalState(null)}
        primaryAction={{
          text:
            addModalState?.key === 'section'
              ? t('features:sections.components.section_tree.add_node_modal.switch_customer')
              : t('features:sections.components.section_tree.add_node_modal.switch_section'),
          disabled:
            (addModalState?.key === 'section' && !!sectionChildren[addModalState.parent ?? '']) ||
            (addModalState?.key === 'customer' &&
              !!sectionUsers[addModalState.parent ?? '']?.length),
          onClick: () =>
            setAddModalState((current) =>
              !current
                ? null
                : { ...current, key: current.key === 'section' ? 'customer' : 'section' },
            ),
        }}
      >
        <table className="w-[200%] table-fixed">
          <colgroup>
            <col className={cn(addModalState?.key === 'customer' && 'collapse')} />
            <col className={cn(addModalState?.key === 'section' && 'collapse')} />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">
                <Heading style="lg" level="h3">
                  {t('features:sections.components.section_tree.add_node_modal.title_section')}
                </Heading>
              </th>
              <th scope="col">
                <Heading style="lg" level="h3">
                  {t('features:sections.components.section_tree.add_node_modal.title_customer')}
                </Heading>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <SectionForm
                  isCreate={
                    !addModalState
                      ? undefined
                      : { parent: addModalState.parent, sibling: addModalState.sibling }
                  }
                  companyId={companyId}
                  defaultValues={{ ...sectionFormCreateDefaultValues }}
                  onCancel={() => setAddModalState(null)}
                  afterSubmit={() => setAddModalState(null)}
                />
              </td>
              <td className="h-0">
                <AddCompanyUser
                  disabled={
                    addModalState?.key !== 'customer' ||
                    !!sectionChildren[addModalState.parent ?? '']?.length
                  }
                  onAddCompanyUser={handleAddCompanyUser}
                  onCancel={() => setAddModalState(null)}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </Modal>
    </ReactFlowProvider>
  );
}
